import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  roleCanEditCatalog,
  TOTE_TYPE_LABELS,
  TOTE_TYPES,
  type PublicTote,
  type ToteListResponse,
  type ToteResponse,
  type ToteType,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Draft {
  number: string;
  type: ToteType;
  notes: string;
}

const EMPTY: Draft = { number: '', type: 'clock_kit', notes: '' };

export function TotesPage(): JSX.Element {
  const { user } = useAuth();
  const canEdit = user ? roleCanEditCatalog(user.role) : false;
  const [totes, setTotes] = useState<PublicTote[]>([]);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<ToteListResponse>('/api/totes');
        if (!cancelled) setTotes(body.totes);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load totes.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<ToteResponse>('/api/totes', {
        method: 'POST',
        body: { number: draft.number, type: draft.type, notes: draft.notes || null },
      });
      setTotes((current) =>
        [...current.filter((entry) => entry.id !== body.tote.id), body.tote].sort((a, b) =>
          a.number.localeCompare(b.number, undefined, { numeric: true }),
        ),
      );
      setDraft(EMPTY);
      setNotice(`Added tote ${body.tote.number}.`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the tote.');
    }
  }

  return (
    <section data-testid="totes-page">
      <div className="card">
        <h2>Totes</h2>
        <p className="meta">
          Numbers are unique and permanent. Retire instead of reusing a number.
        </p>
        {error ? (
          <p className="status status--error" data-testid="totes-error">
            {error}
          </p>
        ) : null}
        {notice ? (
          <p className="status status--ok" data-testid="totes-notice">
            {notice}
          </p>
        ) : null}
      </div>

      {canEdit ? (
        <form className="card" onSubmit={(event) => void onCreate(event)}>
          <h2>Add tote</h2>
          <label className="field">
            Number
            <input
              value={draft.number}
              required
              autoComplete="off"
              onChange={(event) =>
                setDraft((current) => ({ ...current, number: event.target.value }))
              }
              data-testid="tote-number"
            />
          </label>
          <label className="field">
            Type
            <select
              value={draft.type}
              onChange={(event) =>
                setDraft((current) => ({ ...current, type: event.target.value as ToteType }))
              }
              data-testid="tote-type"
            >
              {TOTE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {TOTE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Notes
            <textarea
              value={draft.notes}
              onChange={(event) =>
                setDraft((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </label>
          <button type="submit" className="button" data-testid="tote-create">
            Create tote
          </button>
        </form>
      ) : null}

      {loading ? <p className="meta">Loading totes…</p> : null}

      {totes.map((tote) => (
        <article className="card" key={tote.id} data-testid={`tote-row-${tote.number}`}>
          <h2>
            <Link to={`/totes/${encodeURIComponent(tote.number)}`}>Tote {tote.number}</Link>
          </h2>
          <p className="meta">
            {TOTE_TYPE_LABELS[tote.type]} · {tote.assignable ? 'Assignable' : 'Retired'}
          </p>
        </article>
      ))}
    </section>
  );
}
